import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, PageHeader } from "../components/common";
import { CONTACT_INFO } from "../mock";
import { useToast } from "../hooks/use-toast";
import { submitContact } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete the required fields", description: "Name, email and message are required.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast({ title: "Enquiry sent", description: `Thanks ${form.name} — our Ambala Cantt team will get back to you shortly.` });
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast({ title: "Couldn't send enquiry", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        crumb="Contact"
        title="Talk to Rajiv Syndicate Group"
        intro="Reach our Ambala Cantt head office for accounts queries, feedback or general enquiries."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14">
        {/* Info */}
        <Reveal>
          <Eyebrow>Head office</Eyebrow>
          <h2 className="font-head font-extrabold text-3xl md:text-4xl mt-4">Kuldeep Nagar, GT Road, Ambala Cantt</h2>

          <div className="mt-10 space-y-6">
            <InfoRow icon={Phone} title="Accounts">
              <div className="flex flex-wrap gap-x-2 text-[var(--rs-ink)]">
                <a href="tel:+919215926601" className="hover:text-[var(--rs-blue)]">{CONTACT_INFO.accounts[0]}</a><span className="text-[var(--rs-muted)]">·</span>
                <a href="tel:+919215926645" className="hover:text-[var(--rs-blue)]">{CONTACT_INFO.accounts[1]}</a>
              </div>
              <a href={`mailto:${CONTACT_INFO.accountsEmail}`} className="text-[var(--rs-blue)] text-sm hover:underline">{CONTACT_INFO.accountsEmail}</a>
            </InfoRow>
            <InfoRow icon={Phone} title="Feedback">
              <div className="flex flex-wrap gap-x-2 text-[var(--rs-ink)]">
                <a href="tel:+919215926605" className="hover:text-[var(--rs-blue)]">{CONTACT_INFO.feedback[0]}</a><span className="text-[var(--rs-muted)]">·</span>
                <a href="tel:+919215926607" className="hover:text-[var(--rs-blue)]">{CONTACT_INFO.feedback[1]}</a>
              </div>
              <a href={`mailto:${CONTACT_INFO.feedbackEmail}`} className="text-[var(--rs-blue)] text-sm hover:underline">{CONTACT_INFO.feedbackEmail}</a>
            </InfoRow>
            <InfoRow icon={Mail} title="General">
              <a href={`mailto:${CONTACT_INFO.general}`} className="text-[var(--rs-blue)] hover:underline">{CONTACT_INFO.general}</a>
            </InfoRow>
            <InfoRow icon={MapPin} title="Address">
              <p className="text-[var(--rs-ink)]">{CONTACT_INFO.address.join(" ")}</p>
            </InfoRow>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={120}>
          <div className="bg-white border border-[var(--rs-border)] rounded-2xl p-8 shadow-[0_20px_50px_-30px_rgba(26,92,224,0.4)]">
            <h3 className="font-head font-bold text-2xl">Send an enquiry</h3>
            <form onSubmit={submit} className="mt-6 space-y-5">
              <div>
                <Label className="text-sm font-medium mb-2 block">Name <span className="text-[var(--rs-red)]">*</span></Label>
                <Input value={form.name} onChange={set("name")} placeholder="Your name" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Phone</Label>
                <Input value={form.phone} onChange={set("phone")} placeholder="Phone number" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Email <span className="text-[var(--rs-red)]">*</span></Label>
                <Input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Message <span className="text-[var(--rs-red)]">*</span></Label>
                <Textarea value={form.message} onChange={set("message")} placeholder="How can we help?" rows={5} />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary rounded-lg px-7 py-3.5 text-sm font-semibold w-full disabled:opacity-60">{submitting ? "Sending…" : "Send Enquiry"}</button>
            </form>
          </div>
        </Reveal>
      </section>

      {/* Visit */}
      <section className="bg-[var(--rs-bg)] border-t border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <span className="font-mono-label text-xs text-[var(--rs-blue)] uppercase tracking-wider">Prefer to visit?</span>
            <h3 className="font-head font-bold text-2xl md:text-3xl mt-2">Find your nearest of 13 warehouses & outlets</h3>
          </div>
          <Link to="/locations" className="btn-primary rounded-lg px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 shrink-0">View Locations <ArrowRight size={16} /></Link>
        </div>
      </section>
    </>
  );
}

function InfoRow({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center shrink-0"><Icon size={20} /></div>
      <div>
        <span className="font-mono-label text-xs text-[var(--rs-muted)] uppercase tracking-wider">{title}</span>
        <div className="mt-1">{children}</div>
      </div>
    </div>
  );
}
